'use client';

import { useEffect } from 'react';
// global-error replaces the root layout, so its stylesheet must be imported
// here too or the fallback renders unstyled.
import '@repo/ui/global.css';

/**
 * Last-resort error boundary: renders when the root layout itself throws.
 * Reports to Datadog RUM with route context (reportGlobalError no-ops when
 * RUM is disabled). Deliberately dependency-light — it must render even when
 * the rest of the app is broken — and shows a human-readable message, never
 * the raw error.
 */
export default function GlobalError({
  error
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    import('@/lib/observability')
      .then((mod) =>
        mod.reportGlobalError(error, {
          scope: 'global-error-boundary',
          route: window.location.pathname,
          digest: error.digest
        })
      )
      .catch(() => {
        // Reporting must never throw inside the error boundary.
      });
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-background text-foreground antialiased">
        <main className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-muted-foreground max-w-md text-sm">
            An unexpected error occurred. Your funds and positions are not
            affected — try reloading the page.
          </p>
          {/* A reload, not `reset`: reset renders the same tree again, so
              a root layout that failed (code that did not load, a render
              that throws) usually fails the same way. The copy above
              already asks for a reload. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring mt-2 inline-flex min-h-11 items-center justify-center rounded-md px-6 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
          >
            Try again
          </button>
        </main>
      </body>
    </html>
  );
}
